import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { useProposalData } from '../../context';
import logoGray from '../../assets/logo-gray.png';

export function Footer() {
  const proposal = useProposalData();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-surface-dark text-white overflow-hidden">
      {/* Industrial Pattern Background */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0, 51, 102, 0.5) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 51, 102, 0.5) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Diagonal Accent */}
      <div className="absolute top-0 left-0 right-0 h-32 overflow-hidden">
        <div className="absolute -top-16 left-0 right-0 h-32 bg-gradient-to-r from-hul-red via-hul-red-light to-hul-red transform -skew-y-2 opacity-10" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Logo */}
            <div className="flex items-center gap-3 mb-6">
              <img src={logoGray} alt="Herc-U-Lift" className="h-12 w-auto" />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Your trusted partner in material handling equipment since 1968.
              Sales, service, and solutions for every industry.
            </p>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h3 className="font-heading text-lg uppercase tracking-wider mb-6 flex items-center gap-3">
              <span className="w-8 h-0.5 bg-hul-red" />
              Contact Us
            </h3>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:800-362-3500"
                  className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors"
                >
                  <Phone className="w-5 h-5 text-hul-red mt-0.5" />
                  <div>
                    <div className="font-medium text-white">800-362-3500</div>
                    <div className="text-sm">Toll-Free Sales</div>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href="mailto:sales@herculift.com"
                  className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors"
                >
                  <Mail className="w-5 h-5 text-hul-red mt-0.5" />
                  <div>
                    <div className="font-medium text-white">sales@herculift.com</div>
                    <div className="text-sm">General Inquiries</div>
                  </div>
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-5 h-5 text-hul-red mt-0.5" />
                <div>
                  <div className="font-medium text-white">Multiple Locations</div>
                  <div className="text-sm">Serving the Upper Midwest</div>
                </div>
              </li>
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="font-heading text-lg uppercase tracking-wider mb-6 flex items-center gap-3">
              <span className="w-8 h-0.5 bg-hul-red" />
              Our Services
            </h3>
            <ul className="space-y-3 text-gray-400">
              {[
                'New Equipment Sales',
                'Used & Certified Pre-Owned',
                'Equipment Rentals',
                'Parts & Service',
                'PM Maintenance Programs',
                'Operator Training',
                'Fleet Management',
                'Financing Solutions',
              ].map((service) => (
                <li key={service} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-hul-red/50 rounded-full" />
                  {service}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Your Rep */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="font-heading text-lg uppercase tracking-wider mb-6 flex items-center gap-3">
              <span className="w-8 h-0.5 bg-hul-red" />
              Your Representative
            </h3>
            <div className="bg-white/5 rounded-xl p-5 border border-white/10">
              <div className="flex items-center gap-4 mb-4">
                {proposal.salesperson.photo ? (
                  <img
                    src={proposal.salesperson.photo}
                    alt={proposal.salesperson.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-hul-red"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-hul-navy flex items-center justify-center border-2 border-hul-red">
                    <span className="font-display text-xl">
                      {proposal.salesperson.name.charAt(0)}
                    </span>
                  </div>
                )}
                <div>
                  <div className="font-heading text-lg">{proposal.salesperson.name}</div>
                  <div className="text-sm text-gray-400">{proposal.salesperson.title || 'Equipment Specialist'}</div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <a
                  href={`tel:${proposal.salesperson.phone}`}
                  className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  {proposal.salesperson.phone}
                </a>
                <a
                  href={`mailto:${proposal.salesperson.email}`}
                  className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  {proposal.salesperson.email}
                </a>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-400">
              <Clock className="w-4 h-4" />
              <span>Available Mon-Fri, 7AM-6PM</span>
            </div>
          </motion.div>
        </div>

        {/* Industrial Divider */}
        <div className="relative h-px bg-white/10 my-8">
          <div className="absolute left-0 top-0 w-1/4 h-full bg-gradient-to-r from-hul-red to-transparent" />
          <div className="absolute right-0 top-0 w-1/4 h-full bg-gradient-to-l from-hul-red to-transparent" />
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-400">
          <div className="flex flex-wrap justify-center sm:justify-start gap-4">
            <span>&copy; {currentYear} Herc-U-Lift. All rights reserved.</span>
          </div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
          </div>
        </div>

        {/* Proposal Reference */}
        <div className="mt-6 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full text-xs text-gray-500 font-mono">
            Proposal: {proposal.proposal.id}
          </span>
        </div>
      </div>
    </footer>
  );
}
